/**
 * 订单号管理系统前端类型定义
 */

/**
 * 订单状态枚举
 */
export enum OrderStatus {
  UNUSED = 'unused', // 未使用
  USED = 'used', // 已使用
  EXPIRED = 'expired', // 已过期
  INVALID = 'invalid', // 已作废
}

/**
 * 交易平台枚举
 */
export enum OrderPlatform {
  PDD = 'pdd', // 拼多多
  TAOBAO = 'taobao', // 淘宝
  TMALL = 'tmall', // 天猫
  OTHER = 'other', // 其他
}

/**
 * 订单状态显示文本映射
 */
export const OrderStatusText: Record<OrderStatus, string> = {
  [OrderStatus.UNUSED]: '未使用',
  [OrderStatus.USED]: '已使用',
  [OrderStatus.EXPIRED]: '已过期',
  [OrderStatus.INVALID]: '已作废',
};

/**
 * 交易平台显示文本映射
 */
export const OrderPlatformText: Record<OrderPlatform, string> = {
  [OrderPlatform.PDD]: '拼多多',
  [OrderPlatform.TAOBAO]: '淘宝',
  [OrderPlatform.TMALL]: '天猫',
  [OrderPlatform.OTHER]: '其他',
};

/**
 * 订单号模型
 */
export interface Order {
  id?: number;
  order_no: string; // 订单号
  product_code: string; // 产品代码
  platform: OrderPlatform; // 交易平台
  status: OrderStatus; // 订单状态
  user_id?: string | null; // 使用用户ID
  used_at?: string | null; // 使用时间
  vip_level?: number | null; // 激活的VIP等级
  expire_at?: string | null; // VIP过期时间
  created_by: string; // 创建人ID
  created_at: string; // 创建时间
  updated_at: string; // 更新时间
  notes?: string | null; // 备注
}

/**
 * 创建订单号输入
 */
export interface CreateOrderInput {
  order_no: string; // 订单号
  product_code: string; // 产品代码
  platform: OrderPlatform; // 交易平台
  notes?: string; // 备注
}

/**
 * 批量创建订单号输入
 */
export interface BatchCreateOrdersInput {
  product_code: string; // 产品代码
  platform: OrderPlatform; // 交易平台
  order_nos: string[]; // 订单号列表
  notes?: string; // 备注
  batch_name?: string; // 批次名称
}

/**
 * 更新订单号输入
 */
export interface UpdateOrderInput {
  status?: OrderStatus; // 订单状态
  notes?: string; // 备注
}

/**
 * 订单号查询参数
 */
export interface OrderQueryParams {
  status?: OrderStatus; // 订单状态
  product_code?: string; // 产品代码
  platform?: OrderPlatform; // 交易平台
  user_id?: string; // 用户ID
  created_by?: string; // 创建人
  order_no?: string; // 订单号(模糊查询)
  page?: number; // 页码
  page_size?: number; // 每页数量
  start_date?: string; // 开始日期
  end_date?: string; // 结束日期
}

/**
 * 分页结果
 */
export interface PaginatedResult<T> {
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

/**
 * 订单号统计信息
 */
export interface OrderStatistics {
  total: number; // 总订单数
  unused: number; // 未使用
  used: number; // 已使用
  expired: number; // 已过期
  invalid: number; // 已作废
  by_product: {
    [key: string]: number;
  }; // 按产品代码统计
  by_platform: {
    [key: string]: number;
  }; // 按平台统计
}

/**
 * 订单号验证结果
 */
export interface OrderValidationResult {
  valid: boolean; // 是否有效
  order?: Order; // 订单信息(如果有效)
  error?: string; // 错误信息(如果无效)
}

/**
 * API响应包装类型
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}
